// IndexedDB persistence for SlideCraft (TODO §4.1 / §7.3 / §12 / §17).
//
// db `slidecraft-studio` v7, nine stores:
//   decks     — keyPath `id`      → DeckDocument
//   deckIndex — keyPath `deckId`  → { deckId, title, updatedAt, slideCount, thumbDataUrl? }
//               A listing row per deck (v3, TODO §12.3). The documents home must show 50
//               decks without deserializing 50 decks — a `getAll("decks")` would drag every
//               slide and stylesheet into memory just to render a card.
//   griddDocs — keyPath `docId`   → { docId, owner, doc }, with an `owner` index
//               so a deck's grids can be loaded/cascade-deleted by query, not scan.
//   assets    — keyPath `assetId` → { assetId, owner, blob, mime }, with an `owner`
//               index (v2, TODO §3.2). Image blobs are large and binary, so — unlike
//               grid bodies — they never enter Redux; the deck only stores the assetId
//               reference and blobs are read on demand.
//   nuggets   — keyPath `nuggetId` → NuggetRecord (v6, TODO §17.1.1). One slide, reused
//               across decks. The first object in this database that outlives a deck.
//   nuggetIndex — keyPath `nuggetId` → { nuggetId, name, updatedAt, thumbDataUrl? }
//               (v6, §17.1.2). A listing row per nugget, for exactly the reason `deckIndex`
//               exists: the panel lists fifty nuggets without deserializing fifty slides.
//   diagramDocs — keyPath `docId` → { docId, owner, doc }, with an `owner` index
//               (v4, TODO §21/§4.3.2). The Excalidraw scene behind a `diagram` window.
//               Shaped like `griddDocs` with ONE deliberate difference: the index field is
//               called `owner` and holds an OWNER KEY (`src/lib/ownerKey.ts`), not a raw
//               deck id. §17 will let a knowledge nugget own a body that several decks
//               share, and retrofitting an owner key onto rows already in customers'
//               databases is a data migration — so diagrams are written with one from the
//               first commit (§4.3.3). For a deck-owned body the key IS the deck id, which
//               is why the cascade delete below still works by index query.
//   ganttDocs — keyPath `docId`   → { docId, owner, doc }, with an `owner` index
//               (v5, TODO §26/§3.3). The schedule behind a `gantt` window. Shaped like
//               `diagramDocs` down to the owner key, and written with one from the first
//               commit for the same reason (§3.3.3): retrofitting one onto rows already in
//               customers' databases is a data migration, and §21 proved that the call
//               costs a function call to make and a migration to skip.
//   chartDocs — keyPath `docId`   → { docId, owner, doc }, with an `owner` index
//               (v7, TODO §15/§3.3). The table and encoding behind a `chart` window. The
//               fourth store cast from the same mould, owner key included; the v7 bump adds
//               it and nothing else, so it is additive behind the `contains` guards.
//
// **v5 → v6 is the one migration in this file that rewrites rows.** Until §17 the `griddDocs`
// and `assets` indexes were called `deckId` and held a raw deck id; a nugget's body has no
// deck, so both become `owner` and hold an OWNER KEY (`src/lib/ownerKey.ts`) — the field
// `diagramDocs` and `ganttDocs` were written with from their first commit precisely to avoid
// this. For every existing row the new value IS the old one (`deckOwner(deckId) === deckId`),
// so the backfill is a rename, not a transform, and the cascade delete keeps working
// unchanged. It runs inside the upgrade transaction, so a failure rolls the whole version
// bump back rather than leaving half the rows unreachable.
//
// Every access is guarded: IndexedDB is per-origin and unavailable in Safari private
// browsing, so a failure to open (or any op) degrades to in-memory rather than
// crashing the editor (TODO §4.5). Writes that fail are logged and swallowed.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import { postDeckMessage } from "@/lib/deckChannel";
import { deckOwner, nuggetOwner, ownedByDeck } from "@/lib/ownerKey";
import type { ChartDocument } from "@/types/chart";
import type { DiagramDocument } from "@/types/diagram";
import type { GanttDocument } from "@/types/gantt";
import type { DeckDocument, GriddDocument } from "@/types/document";
import type {
  NuggetIndexEntry,
  NuggetRecord,
  NuggetSlide,
} from "@/types/nugget";

const DB_NAME = "slidecraft-studio";
const DB_VERSION = 7;

// §17.1.3 `owner` is an owner key, not a deck id — see the v5 → v6 note at the top of this
// file. It was called `deckId` until §17 gave a nugget bodies of its own.
export interface GriddDocRecord {
  docId: string;
  owner: string;
  doc: GriddDocument;
}

// §21/§4.3.2 `owner` is an owner key (`ownerKey.ts`), not a deck id — see the note at the
// top of this file. For every row written today it is the deck id, because nuggets (§17)
// do not exist yet; the field is named for what it will hold rather than for what it does.
export interface DiagramDocRecord {
  docId: string;
  owner: string;
  doc: DiagramDocument;
}

// §26/§3.3 Same shape and same `owner` rule as `DiagramDocRecord` — see the note at the top
// of this file.
export interface GanttDocRecord {
  docId: string;
  owner: string;
  doc: GanttDocument;
}

// §15/§3.3.2 The fourth of the same shape. Deliberately not folded into a single generic
// "bodyDocs" store keyed by `(kind, docId)`: four stores mean four independent index queries
// and four independent cascades, and merging them now would be a data migration for every
// existing row to buy nothing but a shorter file.
export interface ChartDocRecord {
  docId: string;
  owner: string;
  doc: ChartDocument;
}

// §17.1.3 `owner` is an owner key, on exactly the terms `GriddDocRecord`'s is.
export interface AssetRecord {
  assetId: string;
  owner: string;
  blob: Blob;
  mime: string;
}

// One listing row per deck. Deliberately small and denormalized: it is read for every card
// on the documents home, and the thumbnail lives HERE rather than on `DeckDocument` (§12.3.2)
// because the deck record is rewritten on every title keystroke and must stay light.
export interface DeckIndexEntry {
  deckId: string;
  title: string;
  updatedAt: string;
  slideCount: number; // active (non-archived) slides
  thumbDataUrl?: string;
}

interface SlideCraftDB extends DBSchema {
  decks: { key: string; value: DeckDocument };
  deckIndex: { key: string; value: DeckIndexEntry };
  griddDocs: {
    key: string;
    value: GriddDocRecord;
    indexes: { owner: string };
  };
  assets: {
    key: string;
    value: AssetRecord;
    indexes: { owner: string };
  };
  nuggets: { key: string; value: NuggetRecord };
  nuggetIndex: { key: string; value: NuggetIndexEntry };
  diagramDocs: {
    key: string;
    value: DiagramDocRecord;
    indexes: { owner: string };
  };
  ganttDocs: {
    key: string;
    value: GanttDocRecord;
    indexes: { owner: string };
  };
  chartDocs: {
    key: string;
    value: ChartDocRecord;
    indexes: { owner: string };
  };
}

// The listing row derived from a deck record. `thumbDataUrl` is not derivable — it is
// written separately by `putDeckThumb` — so it is threaded through from the previous row.
function indexEntryFor(
  deck: DeckDocument,
  thumbDataUrl?: string,
): DeckIndexEntry {
  return {
    deckId: deck.id,
    title: deck.title,
    updatedAt: deck.meta.updatedAt,
    slideCount: deck.slides.filter((s) => !s.archived).length,
    thumbDataUrl,
  };
}

// Cached open promise. Resolves to null (never rejects) when IndexedDB is unavailable,
// so callers can `const db = await getDB(); if (!db) return;` uniformly.
let dbPromise: Promise<IDBPDatabase<SlideCraftDB> | null> | null = null;

function getDB(): Promise<IDBPDatabase<SlideCraftDB> | null> {
  if (!dbPromise) {
    try {
      dbPromise = openDB<SlideCraftDB>(DB_NAME, DB_VERSION, {
        // Each store is created behind a `contains` guard, so the upgrade is additive
        // and runs correctly whether opening from a fresh DB or bumping v1/v2 → v3.
        async upgrade(db, oldVersion, _newVersion, tx) {
          if (!db.objectStoreNames.contains("decks")) {
            db.createObjectStore("decks", { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains("griddDocs")) {
            const store = db.createObjectStore("griddDocs", {
              keyPath: "docId",
            });
            store.createIndex("owner", "owner");
          }
          if (!db.objectStoreNames.contains("assets")) {
            const store = db.createObjectStore("assets", {
              keyPath: "assetId",
            });
            store.createIndex("owner", "owner");
          }
          // §17.1.1/§17.1.2 The nugget shelf. Two stores for the same reason `decks` and
          // `deckIndex` are two: the panel lists the shelf, and a listing row must not drag a
          // slide and its grid bodies into memory to render a tile.
          if (!db.objectStoreNames.contains("nuggets")) {
            db.createObjectStore("nuggets", { keyPath: "nuggetId" });
          }
          if (!db.objectStoreNames.contains("nuggetIndex")) {
            db.createObjectStore("nuggetIndex", { keyPath: "nuggetId" });
          }
          if (!db.objectStoreNames.contains("diagramDocs")) {
            const store = db.createObjectStore("diagramDocs", {
              keyPath: "docId",
            });
            store.createIndex("owner", "owner");
          }
          if (!db.objectStoreNames.contains("ganttDocs")) {
            const store = db.createObjectStore("ganttDocs", {
              keyPath: "docId",
            });
            store.createIndex("owner", "owner");
          }
          // §15/§3.3.2 v6 → v7. Additive like the two before it: a new store with an `owner`
          // index and nothing to backfill, because no chart body can predate the store.
          if (!db.objectStoreNames.contains("chartDocs")) {
            const store = db.createObjectStore("chartDocs", {
              keyPath: "docId",
            });
            store.createIndex("owner", "owner");
          }
          if (!db.objectStoreNames.contains("deckIndex")) {
            db.createObjectStore("deckIndex", { keyPath: "deckId" });
            // v2 → v3 one-time backfill (§12.3.3). A returning user already has decks with
            // no listing row; without this the documents home would come up empty and their
            // work would look lost. Runs inside the upgrade transaction, so a failure rolls
            // the whole version bump back rather than leaving a half-built index.
            if (oldVersion > 0) {
              const index = tx.objectStore("deckIndex");
              for (const deck of await tx.objectStore("decks").getAll()) {
                await index.put(indexEntryFor(deck));
              }
            }
          }
          // v5 → v6: `griddDocs.deckId` and `assets.deckId` become `owner` (§17.1.3). The
          // `contains` guards above skip a store that already exists, so a database coming
          // from v1–v5 still carries the OLD index and the OLD field — this is the half that
          // brings them across. `deckOwner(deckId) === deckId`, so every value is unchanged
          // and only its name moves; a nugget-owned row simply cannot exist yet.
          //
          // Dropping the old index matters as much as adding the new one: left behind it
          // would go stale the first time a nugget body was written, and a stale index is a
          // query that silently returns the wrong rows rather than an error anyone sees.
          if (oldVersion > 0 && oldVersion < 6) {
            for (const name of ["griddDocs", "assets"] as const) {
              // The typed wrapper knows only the CURRENT index names, and this block exists
              // precisely to talk about the old one — so the store is read through a loose
              // view rather than fought with casts at four separate call sites.
              const store = tx.objectStore(name) as unknown as {
                indexNames: DOMStringList;
                createIndex(indexName: string, keyPath: string): unknown;
                deleteIndex(indexName: string): void;
                getAll(): Promise<{ deckId?: string; owner?: string }[]>;
                put(value: unknown): Promise<unknown>;
              };
              if (!store.indexNames.contains("owner")) {
                store.createIndex("owner", "owner");
              }
              if (store.indexNames.contains("deckId")) {
                store.deleteIndex("deckId");
              }
              for (const row of await store.getAll()) {
                const legacyDeckId = row.deckId;
                if (typeof legacyDeckId !== "string") continue;
                const migrated: { deckId?: string; owner?: string } = {
                  ...row,
                  owner: deckOwner(legacyDeckId),
                };
                delete migrated.deckId;
                await store.put(migrated);
              }
            }
          }
        },
        // === The three ways an open can go wrong WITHOUT rejecting (B-hang) ===
        //
        // Without these the app hangs on `DeckSkeleton` forever with an empty console: every
        // entry point awaits `getDB()`, and a blocked open never settles. That is not a
        // theoretical failure — it is what a second tab running an older bundle does.
        //
        // `blocking` is the one that actually breaks the deadlock, and it fires on the page
        // holding the OLD connection: another page wants a version this one is standing on, so
        // close and get out of its way. `dbPromise` is cleared with it, so the next call here
        // reopens at the new version rather than handing out a dead handle.
        blocking() {
          console.warn(
            "[idb] another page needs a newer database version — closing this connection",
          );
          void dbPromise?.then((db) => db?.close());
          dbPromise = null;
        },
        // The mirror image, on the page doing the upgrading: somebody else is holding the old
        // version open and we are waiting on them. Say so — the whole point is that a hang
        // with a reason is a bug report and a silent one is not.
        blocked() {
          console.warn(
            "[idb] database upgrade is blocked by another open tab of this app — close it (or reload it) to continue",
          );
        },
        // The browser dropped the connection under us (storage eviction, a crashed backing
        // store). Drop the cached handle so the next access reopens instead of every call
        // failing against a closed database for the rest of the session.
        terminated() {
          console.warn("[idb] connection terminated by the browser; reopening");
          dbPromise = null;
        },
      }).catch((err) => {
        console.warn(
          "[idb] open failed; persistence disabled for this session",
          err,
        );
        return null;
      });
      // A blocked open never settles and never throws, so nothing downstream can time it out:
      // `listDecks` awaits it, `DeckLanding` awaits that, and the app sits on the skeleton.
      // This does not unblock anything — it makes the wait VISIBLE, which is the part that was
      // missing. Deliberately not a timeout that gives up: an upgrade over a large database is
      // slow but legitimate, and degrading to in-memory half way through one would be worse
      // than waiting.
      const slow = setTimeout(() => {
        console.warn(
          "[idb] still waiting for IndexedDB after 10s — see the blocked warning above, or check Application → IndexedDB in devtools",
        );
      }, 10_000);
      void dbPromise.finally(() => clearTimeout(slow));
    } catch (err) {
      // openDB can throw synchronously if the IndexedDB API is entirely absent.
      console.warn("[idb] IndexedDB unavailable; persistence disabled", err);
      dbPromise = Promise.resolve(null);
    }
  }
  return dbPromise;
}

// === The write counter (TODO §12.5.4) ===
//
// The last `meta.rev` THIS page knows about, per deck. `putDeck` compares it against the
// stored rev inside one transaction and refuses to overwrite a record that moved on without
// us. Web Locks (`deckSession.ts`) is the primary defence and keeps this from ever firing in
// practice; this is the belt-and-braces layer for the contexts where locks are missing
// (§12.5.5), and the only one that survives a page that never took a lock at all.
const knownRevs = new Map<string, number>();

// Record the rev a page just read, so its next write is measured from that base. Called by
// the hydration path; also set by every function here that creates a deck.
export function noteDeckRev(deckId: string, rev: number): void {
  knownRevs.set(deckId, rev);
}

// Did IndexedDB actually open? Callers use this to tell "this deck does not exist" apart
// from "nothing exists, because there is no storage" — two situations that look identical
// from a read but call for opposite responses (§4.5 / §12.1.2).
export async function isPersistenceAvailable(): Promise<boolean> {
  return (await getDB()) !== null;
}

export interface PersistedState {
  deck: DeckDocument | null;
  griddDocs: Record<string, GriddDocument>;
  diagramDocs: Record<string, DiagramDocument>;
  ganttDocs: Record<string, GanttDocument>;
  chartDocs: Record<string, ChartDocument>;
}

// The "nothing" result, in one place rather than three literals that a fifth body store would
// have to find all of.
//
// A FUNCTION, not a shared constant: `readDeckBundle` destructures what this returns and then
// deletes the nuggets' entries out of the maps in place (§17.5.3). Handing every caller the
// same object would make that a mutation of a module-level singleton — harmless today, because
// the maps it deletes from are empty, and precisely the kind of harmless that stops being so
// the first time someone adds a default entry to one of them.
function emptyPersistedState(): PersistedState {
  return {
    deck: null,
    griddDocs: {},
    diagramDocs: {},
    ganttDocs: {},
    chartDocs: {},
  };
}

// Loads one deck and its grid bodies, keyed by docId — the exact shape the hydrated
// reducers expect. `deckId` is the open-deck pointer from the URL (§12.1); a missing
// record resolves to `{ deck: null }` and the caller renders "deck not found" (§12.1.2).
export async function loadPersistedState(
  deckId: string,
): Promise<PersistedState> {
  const db = await getDB();
  if (!db) return emptyPersistedState();
  try {
    const deck = (await db.get("decks", deckId)) ?? null;
    const griddDocs: Record<string, GriddDocument> = {};
    const diagramDocs: Record<string, DiagramDocument> = {};
    const ganttDocs: Record<string, GanttDocument> = {};
    const chartDocs: Record<string, ChartDocument> = {};
    if (deck) {
      // §17.1.3 By OWNER key, exactly like the two queries below it since v6 — so a
      // nugget-owned grid body is NOT in this result set and is loaded by the nugget pass
      // at the foot of this function instead.
      const records = await db.getAllFromIndex(
        "griddDocs",
        "owner",
        deckOwner(deck.id),
      );
      for (const record of records) griddDocs[record.docId] = record.doc;
      // §21/§4.3.2 By OWNER key. Today that is the deck id, so this reads exactly like the
      // line above it; once §17 lands, a nugget-owned body is not in this query's results
      // and is loaded by the nugget link instead — which is the whole point of the key.
      const diagrams = await db.getAllFromIndex(
        "diagramDocs",
        "owner",
        deckOwner(deck.id),
      );
      for (const record of diagrams) diagramDocs[record.docId] = record.doc;
      // §26/§3.3 By OWNER key, exactly as the diagram query above.
      const gantts = await db.getAllFromIndex(
        "ganttDocs",
        "owner",
        deckOwner(deck.id),
      );
      for (const record of gantts) ganttDocs[record.docId] = record.doc;
      // §15/§3.3.5 By OWNER key, exactly as the gantt query above.
      const charts = await db.getAllFromIndex(
        "chartDocs",
        "owner",
        deckOwner(deck.id),
      );
      for (const record of charts) chartDocs[record.docId] = record.doc;

      // §17.1.5 …and then the bodies this deck does not own. A linked slide keeps the
      // NUGGET's ids verbatim, and those rows are filed under `nugget:<id>` — invisible to
      // all three queries above, which is exactly what keeps `deleteDeckCascade` from
      // reaching them. Without this pass a linked slide would hydrate with its content
      // window pointing at a body that is in the database but not in the store, and render
      // as an empty grid.
      //
      // One query per store per DISTINCT nugget, not per slide: §17.2.6 caps a nugget at one
      // slide per deck today, but the de-duplication costs a `Set` and survives lifting it.
      const linkedNuggetIds = new Set(
        deck.slides.flatMap((slide) =>
          slide.link ? [slide.link.nuggetId] : [],
        ),
      );
      for (const nuggetId of linkedNuggetIds) {
        const owner = nuggetOwner(nuggetId);
        for (const record of await db.getAllFromIndex(
          "griddDocs",
          "owner",
          owner,
        )) {
          griddDocs[record.docId] = record.doc;
        }
        for (const record of await db.getAllFromIndex(
          "diagramDocs",
          "owner",
          owner,
        )) {
          diagramDocs[record.docId] = record.doc;
        }
        for (const record of await db.getAllFromIndex(
          "ganttDocs",
          "owner",
          owner,
        )) {
          ganttDocs[record.docId] = record.doc;
        }
        for (const record of await db.getAllFromIndex(
          "chartDocs",
          "owner",
          owner,
        )) {
          chartDocs[record.docId] = record.doc;
        }
      }
    }
    return { deck, griddDocs, diagramDocs, ganttDocs, chartDocs };
  } catch (err) {
    console.warn("[idb] load failed; starting empty", err);
    return emptyPersistedState();
  }
}

// Every deck's listing row, most recently updated first — what the documents home (§12.4.1)
// and the TopBar switcher (§12.4.2) render from.
export async function listDecks(): Promise<DeckIndexEntry[]> {
  const db = await getDB();
  if (!db) return [];
  try {
    const rows = await db.getAll("deckIndex");
    return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch (err) {
    console.warn("[idb] listDecks failed", err);
    return [];
  }
}

export async function getDeck(deckId: string): Promise<DeckDocument | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    return (await db.get("decks", deckId)) ?? null;
  } catch (err) {
    console.warn("[idb] getDeck failed", err);
    return null;
  }
}

// "conflict" = another page wrote a newer rev; the caller must re-hydrate rather than
// retry, because whatever it holds is built on a base that no longer exists.
// "unavailable" = persistence is off (or the write threw) — the editor keeps working
// in-memory, exactly as it does when IndexedDB never opened.
export type PutDeckResult = "ok" | "conflict" | "unavailable";

// Writes the deck record AND its listing row in one transaction, stamping the next `rev`
// (§12.5.4). Read-compare-write: the stored rev is read inside the same transaction that
// writes, so two pages racing cannot both pass the check.
export async function putDeck(deck: DeckDocument): Promise<PutDeckResult> {
  const db = await getDB();
  if (!db) return "unavailable";
  try {
    const tx = db.transaction(["decks", "deckIndex"], "readwrite");
    const decks = tx.objectStore("decks");
    const index = tx.objectStore("deckIndex");

    const stored = await decks.get(deck.id);
    const storedRev = stored?.meta.rev ?? 0;
    // First write of a deck this page never read (import/duplicate) measures from the
    // stored value, so a fresh record is not treated as a conflict with itself.
    const known = knownRevs.get(deck.id) ?? storedRev;
    if (stored && storedRev > known) {
      tx.abort();
      // The abort rejects tx.done; that rejection is the expected outcome here.
      await tx.done.catch(() => {});
      return "conflict";
    }

    const rev = storedRev + 1;
    const record: DeckDocument = { ...deck, meta: { ...deck.meta, rev } };
    await decks.put(record);
    // Preserve the thumbnail: it is written on its own cadence by `putDeckThumb` and is
    // not derivable from the deck record.
    const prev = await index.get(deck.id);
    await index.put(indexEntryFor(record, prev?.thumbDataUrl));
    await tx.done;

    knownRevs.set(deck.id, rev);
    postDeckMessage({ type: "deck-updated", deckId: deck.id, rev });
    return "ok";
  } catch (err) {
    console.warn("[idb] putDeck failed", err);
    return "unavailable";
  }
}

// First write of a brand-new deck: no rev check (there is nothing to conflict with) and no
// `deck-updated` broadcast — nobody has it open yet. `deck-created` refreshes deck lists.
export async function createDeck(deck: DeckDocument): Promise<void> {
  const record: DeckDocument = { ...deck, meta: { ...deck.meta, rev: 0 } };
  knownRevs.set(record.id, 0);
  const db = await getDB();
  if (!db) return;
  try {
    const tx = db.transaction(["decks", "deckIndex"], "readwrite");
    await tx.objectStore("decks").put(record);
    await tx.objectStore("deckIndex").put(indexEntryFor(record));
    await tx.done;
    postDeckMessage({ type: "deck-created", deckId: record.id });
  } catch (err) {
    console.warn("[idb] createDeck failed", err);
  }
}

// The deck card's preview image (§12.3.2) — the first active slide's thumbnail, which
// `OffscreenSlideCapturer` already produces for the slide nav. Touches only the listing
// row, so it never contends with a deck write or bumps `rev`.
export async function putDeckThumb(
  deckId: string,
  thumbDataUrl: string,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    const tx = db.transaction("deckIndex", "readwrite");
    const prev = await tx.store.get(deckId);
    // No row means the deck is gone (or was never written) — don't resurrect it.
    if (prev) await tx.store.put({ ...prev, thumbDataUrl });
    await tx.done;
  } catch (err) {
    console.warn("[idb] putDeckThumb failed", err);
  }
}

// Delete a deck and everything hanging off it (§12.4.4), in ONE transaction so a failure
// can't leave half a deck behind. Grid bodies and image blobs go by the `deckId` index —
// orphaned blobs are quota the user has no way to see, let alone reclaim.
export async function deleteDeckCascade(deckId: string): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    const tx = db.transaction(
      [
        "decks",
        "deckIndex",
        "griddDocs",
        "assets",
        "diagramDocs",
        "ganttDocs",
        "chartDocs",
      ],
      "readwrite",
    );
    // §17.5.2 Deck-owned grid bodies only, by OWNER key — so a nugget-owned body shared by
    // four other decks is not in the result set and survives this deck being deleted. That
    // is not a gap in the cascade: since v6 it is the cascade's definition, and the same
    // sentence already applied to diagrams and gantts below.
    const grids = tx.objectStore("griddDocs");
    for (const key of await grids
      .index("owner")
      .getAllKeys(deckOwner(deckId))) {
      await grids.delete(key);
    }
    // §21/§4.3.2 Deck-owned diagram bodies only: the index is queried with this deck's
    // OWNER key, so a nugget-owned body (§17) is not in the result set and survives the
    // deck being deleted, which is the correct behaviour rather than a gap.
    const diagrams = tx.objectStore("diagramDocs");
    for (const key of await diagrams
      .index("owner")
      .getAllKeys(deckOwner(deckId))) {
      await diagrams.delete(key);
    }
    // §26/§3.3 Deck-owned gantt bodies, on exactly the terms the diagrams above get.
    const gantts = tx.objectStore("ganttDocs");
    for (const key of await gantts
      .index("owner")
      .getAllKeys(deckOwner(deckId))) {
      await gantts.delete(key);
    }
    // §15/§3.3.2 Deck-owned chart bodies, on exactly the terms the gantts above get.
    const charts = tx.objectStore("chartDocs");
    for (const key of await charts
      .index("owner")
      .getAllKeys(deckOwner(deckId))) {
      await charts.delete(key);
    }
    // §17.5.2 …and the image blobs, on exactly the same terms.
    const assets = tx.objectStore("assets");
    for (const key of await assets
      .index("owner")
      .getAllKeys(deckOwner(deckId))) {
      await assets.delete(key);
    }
    await tx.objectStore("deckIndex").delete(deckId);
    await tx.objectStore("decks").delete(deckId);
    await tx.done;
    knownRevs.delete(deckId);
    postDeckMessage({ type: "deck-deleted", deckId });
  } catch (err) {
    console.warn("[idb] deleteDeckCascade failed", err);
  }
}

// §17.1.3 Takes an OWNER KEY, not a deck id. Callers resolve it from the slide that holds
// this `griddDocId` (`ownerForContentDoc`), because on a linked slide that owner is the
// nugget and writing the body under the deck would quietly fork it.
export async function putGriddDoc(
  owner: string,
  docId: string,
  doc: GriddDocument,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    await db.put("griddDocs", { docId, owner, doc });
  } catch (err) {
    console.warn("[idb] putGriddDoc failed", err);
  }
}

// §17.6.2 THE OWNERSHIP GATE for grid bodies — the twin of `deleteDiagramDocIfOwnedBy`
// below, and the one this whole section of §17 was written around. A deck may delete a grid
// body only if it owns it; a nugget-owned body is shared by every deck that links the
// nugget, so this deck dropping the slide must unlink it and nothing more. Read-compare-
// delete inside ONE transaction, for the reason spelled out on the diagram version.
//
// This replaced an unconditional `deleteGriddDoc`. There is deliberately no unconditional
// variant left: a caller that wants one is a caller about to reintroduce the bug.
export async function deleteGriddDocIfOwnedBy(
  docId: string,
  deckId: string,
): Promise<boolean> {
  const db = await getDB();
  if (!db) return false;
  try {
    const tx = db.transaction("griddDocs", "readwrite");
    const record = await tx.store.get(docId);
    if (!record || !ownedByDeck(record.owner, deckId)) {
      await tx.done;
      return false;
    }
    await tx.store.delete(docId);
    await tx.done;
    return true;
  } catch (err) {
    console.warn("[idb] deleteGriddDocIfOwnedBy failed", err);
    return false;
  }
}

// === Diagram bodies (TODO §21/§4.3.2) ===

export async function putDiagramDoc(
  owner: string,
  docId: string,
  doc: DiagramDocument,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    await db.put("diagramDocs", { docId, owner, doc });
  } catch (err) {
    console.warn("[idb] putDiagramDoc failed", err);
  }
}

export async function getDiagramDoc(
  docId: string,
): Promise<DiagramDocRecord | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    return (await db.get("diagramDocs", docId)) ?? null;
  } catch (err) {
    console.warn("[idb] getDiagramDoc failed", err);
    return null;
  }
}

// §4.3.4 / §17.6.2 THE OWNERSHIP GATE, as code rather than as a comment. A deck may delete
// a diagram body only if it owns it; a nugget-owned body is shared by every deck that links
// the nugget, so this deck dropping the slide must unlink it and nothing more. Returns
// whether the row was actually deleted, so a caller can say something honest if it ever
// needs to.
//
// Read-compare-delete inside ONE transaction, for the same reason `putDeck` reads the rev
// inside its own: a check made outside the transaction is a check made against a value that
// may have changed by the time the delete lands.
export async function deleteDiagramDocIfOwnedBy(
  docId: string,
  deckId: string,
): Promise<boolean> {
  const db = await getDB();
  if (!db) return false;
  try {
    const tx = db.transaction("diagramDocs", "readwrite");
    const record = await tx.store.get(docId);
    // Already gone — nothing to do, and not an error: two windows can drop the same
    // window, and Clear Slide runs after a layout collapse already cleaned up.
    if (!record) {
      await tx.done;
      return false;
    }
    if (!ownedByDeck(record.owner, deckId)) {
      await tx.done;
      return false;
    }
    await tx.store.delete(docId);
    await tx.done;
    return true;
  } catch (err) {
    console.warn("[idb] deleteDiagramDocIfOwnedBy failed", err);
    return false;
  }
}

// === Gantt bodies (TODO §26/§3.3) ===
//
// A line-for-line copy of the three diagram functions above, ownership gate included: the
// problem has the same shape, and a schedule is if anything more likely to be shared through
// a §17 nugget than a drawing is.

export async function putGanttDoc(
  owner: string,
  docId: string,
  doc: GanttDocument,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    await db.put("ganttDocs", { docId, owner, doc });
  } catch (err) {
    console.warn("[idb] putGanttDoc failed", err);
  }
}

export async function getGanttDoc(
  docId: string,
): Promise<GanttDocRecord | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    return (await db.get("ganttDocs", docId)) ?? null;
  } catch (err) {
    console.warn("[idb] getGanttDoc failed", err);
    return null;
  }
}

// §3.3.4 / §17.6.2 The ownership gate — read-compare-delete inside ONE transaction, for the
// reason spelled out on `deleteDiagramDocIfOwnedBy`.
export async function deleteGanttDocIfOwnedBy(
  docId: string,
  deckId: string,
): Promise<boolean> {
  const db = await getDB();
  if (!db) return false;
  try {
    const tx = db.transaction("ganttDocs", "readwrite");
    const record = await tx.store.get(docId);
    if (!record) {
      await tx.done;
      return false;
    }
    if (!ownedByDeck(record.owner, deckId)) {
      await tx.done;
      return false;
    }
    await tx.store.delete(docId);
    await tx.done;
    return true;
  } catch (err) {
    console.warn("[idb] deleteGanttDocIfOwnedBy failed", err);
    return false;
  }
}

// === Chart bodies (TODO §15/§3.3) ===
//
// The gantt trio again, unchanged down to the ownership gate. A chart is if anything the most
// likely of the four to travel through a §17 nugget — a table of numbers is exactly the kind
// of slide several decks want to share — so the gate matters here rather than being inherited
// out of symmetry.

export async function putChartDoc(
  owner: string,
  docId: string,
  doc: ChartDocument,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    await db.put("chartDocs", { docId, owner, doc });
  } catch (err) {
    console.warn("[idb] putChartDoc failed", err);
  }
}

export async function getChartDoc(
  docId: string,
): Promise<ChartDocRecord | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    return (await db.get("chartDocs", docId)) ?? null;
  } catch (err) {
    console.warn("[idb] getChartDoc failed", err);
    return null;
  }
}

// §3.3.4 / §17.6.2 The ownership gate — read-compare-delete inside ONE transaction, for the
// reason spelled out on `deleteDiagramDocIfOwnedBy`.
export async function deleteChartDocIfOwnedBy(
  docId: string,
  deckId: string,
): Promise<boolean> {
  const db = await getDB();
  if (!db) return false;
  try {
    const tx = db.transaction("chartDocs", "readwrite");
    const record = await tx.store.get(docId);
    if (!record) {
      await tx.done;
      return false;
    }
    if (!ownedByDeck(record.owner, deckId)) {
      await tx.done;
      return false;
    }
    await tx.store.delete(docId);
    await tx.done;
    return true;
  } catch (err) {
    console.warn("[idb] deleteChartDocIfOwnedBy failed", err);
    return false;
  }
}

// === Image assets (TODO §3.2) ===
//
// Blobs live only here, never in Redux. The content record holds the assetId; blobs are
// read on demand (useImageAsset) and cleaned up on Clear Slide (orphan rule, §3.5).

// §17.1.3 `owner` is an OWNER KEY, not a deck id — the same change `putGriddDoc` took, and
// for the same reason: an image dropped onto a linked slide belongs to the nugget.
export async function putAsset(
  assetId: string,
  owner: string,
  blob: Blob,
  mime: string,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    await db.put("assets", { assetId, owner, blob, mime });
  } catch (err) {
    console.warn("[idb] putAsset failed", err);
  }
}

export async function getAsset(assetId: string): Promise<AssetRecord | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    return (await db.get("assets", assetId)) ?? null;
  } catch (err) {
    console.warn("[idb] getAsset failed", err);
    return null;
  }
}

// Every asset blob under one owner — the export path (§12.6.1) needs them all, and walking
// the slides would miss nothing but cost a scan per image. §17.1.3: by owner key, so a
// deck's export carries the deck's blobs and a nugget's carries the nugget's.
export async function getOwnedAssets(owner: string): Promise<AssetRecord[]> {
  const db = await getDB();
  if (!db) return [];
  try {
    return await db.getAllFromIndex("assets", "owner", owner);
  } catch (err) {
    console.warn("[idb] getOwnedAssets failed", err);
    return [];
  }
}

// Unconditional delete. Still correct for the two callers that own the blob outright by
// construction — the deck logo (§24.0.4, deck-level and never a nugget's) and the nugget
// deletion sweep below. Everything reachable from a SLIDE goes through the gated version.
export async function deleteAsset(assetId: string): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    await db.delete("assets", assetId);
  } catch (err) {
    console.warn("[idb] deleteAsset failed", err);
  }
}

// §17.6.2 The ownership gate for image blobs — the third of the four, and the last piece of
// "a slide in this deck may not delete something four other decks are rendering".
export async function deleteAssetIfOwnedBy(
  assetId: string,
  deckId: string,
): Promise<boolean> {
  const db = await getDB();
  if (!db) return false;
  try {
    const tx = db.transaction("assets", "readwrite");
    const record = await tx.store.get(assetId);
    if (!record || !ownedByDeck(record.owner, deckId)) {
      await tx.done;
      return false;
    }
    await tx.store.delete(assetId);
    await tx.done;
    return true;
  } catch (err) {
    console.warn("[idb] deleteAssetIfOwnedBy failed", err);
    return false;
  }
}

// === Knowledge nuggets (TODO §17.1) ===
//
// A nugget is the first record here that belongs to no deck. Its bodies and blobs live in
// the same `griddDocs` / `diagramDocs` / `ganttDocs` / `assets` stores as everything else,
// under `nuggetOwner(nuggetId)` — which is what makes `deleteDeckCascade` unable to reach
// them (§17.1.3) and why `getAsset`, `useImageAsset` and every renderer needed no branch.
//
// `rev` is owned here and nowhere else, exactly like `DeckMeta.rev` (§12.5.4): every write
// reads the stored value inside the same transaction that writes and stamps `rev + 1`. A
// caller never supplies one, so two pages projecting into the same nugget cannot both
// believe they produced revision 7.

// The listing row derived from a record. `thumbDataUrl` is not derivable — it is written
// separately by `putNuggetThumb` — so it is threaded through from the previous row, exactly
// as `indexEntryFor` does for decks.
function nuggetEntryFor(
  record: NuggetRecord,
  thumbDataUrl?: string,
): NuggetIndexEntry {
  return {
    nuggetId: record.nuggetId,
    // §17.4.6 The name IS the slide's title. No second field, and no invented default
    // stored: an untitled nugget is stored untitled and LABELLED in the panel.
    name: record.slide.title,
    updatedAt: record.updatedAt,
    thumbDataUrl,
  };
}

// §17.1.2 The shelf, most recently updated first — what the nugget panel renders from.
export async function listNuggets(): Promise<NuggetIndexEntry[]> {
  const db = await getDB();
  if (!db) return [];
  try {
    const rows = await db.getAll("nuggetIndex");
    return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch (err) {
    console.warn("[idb] listNuggets failed", err);
    return [];
  }
}

export async function getNugget(
  nuggetId: string,
): Promise<NuggetRecord | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    return (await db.get("nuggets", nuggetId)) ?? null;
  } catch (err) {
    console.warn("[idb] getNugget failed", err);
    return null;
  }
}

// Several nuggets in one round trip — the reconcile pass (§17.2.5) asks for every nugget a
// deck links, and a deck with eight linked slides should not cost eight transactions.
// A missing nugget is simply absent from the map; the caller drops that link (§17.2.5).
export async function getNuggets(
  nuggetIds: string[],
): Promise<Record<string, NuggetRecord>> {
  const out: Record<string, NuggetRecord> = {};
  if (nuggetIds.length === 0) return out;
  const db = await getDB();
  if (!db) return out;
  try {
    const tx = db.transaction("nuggets", "readonly");
    for (const id of new Set(nuggetIds)) {
      const record = await tx.store.get(id);
      if (record) out[id] = record;
    }
    await tx.done;
  } catch (err) {
    console.warn("[idb] getNuggets failed", err);
  }
  return out;
}

// §17.2.2 The projection's write. Takes the slide as it should now be and stamps the next
// `rev`, returning it so the caller can record it on `slide.link.rev`. Returns null when
// nothing could be written (no storage, or the nugget is gone) — the caller must not then
// tell the deck it is in step with a revision that does not exist.
//
// `createdAt`/`origin` come from the stored record when there is one, so a projection can
// never rewrite a nugget's history; `slide` and `updatedAt` are what it changes.
export async function putNuggetSlide(
  nuggetId: string,
  slide: NuggetSlide,
): Promise<number | null> {
  const db = await getDB();
  if (!db) return null;
  try {
    const tx = db.transaction(["nuggets", "nuggetIndex"], "readwrite");
    const nuggets = tx.objectStore("nuggets");
    const stored = await nuggets.get(nuggetId);
    if (!stored) {
      // §17.2.5's other half: the nugget was deleted from the shelf while a deck that links
      // it is open. Writing it back here would resurrect a record the user deleted, so the
      // projection is dropped and the link falls away on the next open.
      await tx.done;
      return null;
    }
    const now = new Date().toISOString();
    const record: NuggetRecord = {
      ...stored,
      slide,
      rev: stored.rev + 1,
      updatedAt: now,
    };
    await nuggets.put(record);
    const index = tx.objectStore("nuggetIndex");
    const prev = await index.get(nuggetId);
    await index.put(nuggetEntryFor(record, prev?.thumbDataUrl));
    await tx.done;
    postDeckMessage({ type: "nugget-updated", nuggetId, rev: record.rev });
    return record.rev;
  } catch (err) {
    console.warn("[idb] putNuggetSlide failed", err);
    return null;
  }
}

// §17.4.4 First write of a brand-new nugget: rev 0, no broadcast (nothing links it yet).
// The caller has already copied the bodies and blobs to this nugget's owner key.
export async function createNugget(
  nuggetId: string,
  slide: NuggetSlide,
): Promise<NuggetRecord | null> {
  const db = await getDB();
  if (!db) return null;
  const now = new Date().toISOString();
  const record: NuggetRecord = {
    nuggetId,
    slide,
    rev: 0,
    createdAt: now,
    updatedAt: now,
    origin: "local",
  };
  try {
    const tx = db.transaction(["nuggets", "nuggetIndex"], "readwrite");
    await tx.objectStore("nuggets").put(record);
    await tx.objectStore("nuggetIndex").put(nuggetEntryFor(record));
    await tx.done;
    return record;
  } catch (err) {
    console.warn("[idb] createNugget failed", err);
    return null;
  }
}

// §17.5.3 Import's "adopt or create". Writes a record verbatim — rev included — and is used
// ONLY for a nugget arriving from a `.deckai` archive that this database has never seen.
// A nugget that already exists locally is adopted, never overwritten, whatever its rev: a
// file is a snapshot someone mailed you, not a sync channel.
export async function putNuggetRecord(record: NuggetRecord): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    const tx = db.transaction(["nuggets", "nuggetIndex"], "readwrite");
    await tx.objectStore("nuggets").put(record);
    const index = tx.objectStore("nuggetIndex");
    const prev = await index.get(record.nuggetId);
    await index.put(nuggetEntryFor(record, prev?.thumbDataUrl));
    await tx.done;
  } catch (err) {
    console.warn("[idb] putNuggetRecord failed", err);
  }
}

// §17.4.5 The tile image, captured off the deck's own slide thumbnail rather than by a
// second capturer. Touches only the listing row, so it never contends with a projection or
// bumps `rev` — `putDeckThumb`'s rule, applied to the shelf.
export async function putNuggetThumb(
  nuggetId: string,
  thumbDataUrl: string,
): Promise<void> {
  const db = await getDB();
  if (!db) return;
  try {
    const tx = db.transaction("nuggetIndex", "readwrite");
    const prev = await tx.store.get(nuggetId);
    // No row means the nugget is gone — don't resurrect it.
    if (prev) await tx.store.put({ ...prev, thumbDataUrl });
    await tx.done;
  } catch (err) {
    console.warn("[idb] putNuggetThumb failed", err);
  }
}

// §17.6.1 Delete a nugget and the bodies and blobs it owns, in ONE transaction. Decks are
// deliberately NOT touched: they keep the slide exactly as it is and drop the dead link on
// their next open (§17.2.5). A nugget deleted on the shelf must never blank a slide in a
// deck that used it.
//
// This is the mirror image of `deleteDeckCascade` and it is safe for the same reason: every
// row it deletes is filed under this nugget's owner key, which no deck's query can return.
export async function deleteNuggetCascade(nuggetId: string): Promise<void> {
  const db = await getDB();
  if (!db) return;
  const owner = nuggetOwner(nuggetId);
  try {
    const tx = db.transaction(
      [
        "nuggets",
        "nuggetIndex",
        "griddDocs",
        "assets",
        "diagramDocs",
        "ganttDocs",
        "chartDocs",
      ],
      "readwrite",
    );
    for (const name of [
      "griddDocs",
      "diagramDocs",
      "ganttDocs",
      "chartDocs",
      "assets",
    ] as const) {
      const store = tx.objectStore(name);
      for (const key of await store.index("owner").getAllKeys(owner)) {
        await store.delete(key);
      }
    }
    await tx.objectStore("nuggetIndex").delete(nuggetId);
    await tx.objectStore("nuggets").delete(nuggetId);
    await tx.done;
    postDeckMessage({ type: "nugget-deleted", nuggetId });
  } catch (err) {
    console.warn("[idb] deleteNuggetCascade failed", err);
  }
}

// Everything one nugget owns, for the `.deckai` export (§17.5.3) and for Break link
// (§17.4.3), both of which need the bodies rather than just the record.
export async function loadNuggetBodies(nuggetId: string): Promise<{
  griddDocs: Record<string, GriddDocument>;
  diagramDocs: Record<string, DiagramDocument>;
  ganttDocs: Record<string, GanttDocument>;
  chartDocs: Record<string, ChartDocument>;
  assets: AssetRecord[];
}> {
  const empty = {
    griddDocs: {},
    diagramDocs: {},
    ganttDocs: {},
    chartDocs: {},
    assets: [] as AssetRecord[],
  };
  const db = await getDB();
  if (!db) return empty;
  const owner = nuggetOwner(nuggetId);
  try {
    const griddDocs: Record<string, GriddDocument> = {};
    for (const r of await db.getAllFromIndex("griddDocs", "owner", owner)) {
      griddDocs[r.docId] = r.doc;
    }
    const diagramDocs: Record<string, DiagramDocument> = {};
    for (const r of await db.getAllFromIndex("diagramDocs", "owner", owner)) {
      diagramDocs[r.docId] = r.doc;
    }
    const ganttDocs: Record<string, GanttDocument> = {};
    for (const r of await db.getAllFromIndex("ganttDocs", "owner", owner)) {
      ganttDocs[r.docId] = r.doc;
    }
    const chartDocs: Record<string, ChartDocument> = {};
    for (const r of await db.getAllFromIndex("chartDocs", "owner", owner)) {
      chartDocs[r.docId] = r.doc;
    }
    const assets = await db.getAllFromIndex("assets", "owner", owner);
    return { griddDocs, diagramDocs, ganttDocs, chartDocs, assets };
  } catch (err) {
    console.warn("[idb] loadNuggetBodies failed", err);
    return empty;
  }
}

// §17.6.1 "…used in 3 decks." A scan of the deck records at dialog time rather than a
// maintained reference count, deliberately: a count kept up to date by every insert, break,
// duplicate, import and purge is a count that drifts, and it drifts silently. There are
// tens of decks, not thousands, and this runs on one click.
export async function countDecksUsingNugget(nuggetId: string): Promise<number> {
  const db = await getDB();
  if (!db) return 0;
  try {
    const decks = await db.getAll("decks");
    return decks.filter((deck) =>
      deck.slides.some((slide) => slide.link?.nuggetId === nuggetId),
    ).length;
  } catch (err) {
    console.warn("[idb] countDecksUsingNugget failed", err);
    return 0;
  }
}
